import { Component, DestroyRef, HostListener, OnDestroy, OnInit, ViewChild, signal, computed, effect, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { AuthenticationService, DecodedJwt } from '@colimo/colimo-ui-library';
import { ColimindDebugService } from '../../../services/colimind/colimind-debug.service';
import { ColimindService } from '../service/colimind.service';
import { ColimindSettingsService } from '../service/colimind-settings.service';
import { LanguagesService } from '../../../services/languages/languages.service';
import { SessionListComponent } from '../session-list/colimind-session-list.component';
import { ChatWindowComponent } from '../chat-window/colimind-chat-window.component';
import { DashboardComponent } from '../dashboard/colimind-dashboard.component';
import { ColimindSettingsPanelComponent } from '../settings-panel/colimind-settings-panel.component';
import { OnboardingComponent } from '../onboarding/colimind-onboarding.component';
import type { Message, SessionSummary, ChatResponse, SessionListItem, PanelMode } from '../colimind-types';
import {ColimindApiService, ColimindChatRequest, ColimindChatStreamEvent, toChatResponse} from "../service/colimind-api.service";
import { firstNameFromSubject, formatAgentAnswers, isTypingTarget, matchSessions, parseAgentQuestions, toolStatusKey } from '../colimind-utils';

export type { PanelMode };

// Keep in sync with the fade of `.chat-not-found` in the dashboard styles.
const CHAT_NOT_FOUND_MS = 5000;

@Component({
  selector: 'colimo-colimind-page',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatButtonModule, MatTooltipModule, MatSidenavModule, MatInputModule, MatFormFieldModule, TranslateModule, SessionListComponent, ChatWindowComponent, DashboardComponent, ColimindSettingsPanelComponent, OnboardingComponent],
  templateUrl: './colimind-page.component.html',
  styleUrls: ['./colimind-page.component.scss'],
})
export class ColimindPageComponent implements OnInit, OnDestroy {
  readonly debug = inject(ColimindDebugService);
  private colimind = inject(ColimindService);
  private translate = inject(TranslateService);
  private colimindApi = inject(ColimindApiService);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthenticationService);
  private router = inject(Router);
  private languages = inject(LanguagesService);
  readonly settings = inject(ColimindSettingsService);

  @HostListener('document:keydown', ['$event']) onKey(e: KeyboardEvent): void {
    this.dismissSpotlight();
    // Typing "ddd" into the chat must not switch on the debug mode
    if (!isTypingTarget(e.target)) this.debug.onKeydown(e);
  }

  /** The cue is a hint, not a step: the first interaction of any kind ends it. */
  @HostListener('document:click') onDocumentClick(): void { this.dismissSpotlight(); }

  /**
   * Closing the tab or the browser does not run ngOnDestroy, so a partial answer would be lost
   * without a trace. `pagehide` still fires (unlike `beforeunload` on mobile and with bfcache),
   * which leaves just enough room to mark and persist what already arrived.
   *
   * Deliberately NOT wired to `visibilitychange`: a tab switch keeps the stream alive, and the
   * spec wants the request to finish in the background rather than be cancelled.
   */
  @HostListener('window:pagehide') onPageHide(): void { this.abortPendingRequest(); }

  messages = signal<Message[]>([]);
  loading = signal(false);
  /** Translation key of the current agent step, shown while loading. */
  streamStatus = signal<string | null>(null);
  sessions = signal<SessionSummary[]>([]);
  activeSessionId = signal<string | undefined>(undefined);
  panelOpen = signal(false);
  panelMode = signal<PanelMode>('history');
  searchQuery = signal('');
  /** Set when a chat link could not be opened, e.g. a link to somebody else's chat. */
  chatNotFound = signal(false);
  private chatNotFoundTimer?: ReturnType<typeof setTimeout>;
  /** Name from the JWT, used until the user states how they want to be addressed. */
  private jwtName = signal('');

  /**
   * The name shown in the greeting. The profile setting wins over the JWT and takes effect the
   * moment it is saved, which is what the "set how I am addressed personally" story asks for.
   */
  userName = computed(() => this.settings.settings().addressing.trim() || this.jwtName());
  activeSuggestions = this.settings.activeSuggestions;
  activeDefaults = this.settings.activeDefaults;

  private streamSub?: Subscription;
  private userIdentifier: string;
  /** The URL follows the open chat only after the chat from the URL has been opened. */
  private readonly urlApplied = signal(false);
  /** URL this page navigated to itself; its NavigationEnd is not a user action. */
  private ownNavigation?: string;
  /** The assistant message currently being streamed into, if any. */
  private pendingMessage?: Message;
  /** Last stream event that identified its thread -- needed to persist an interrupted answer. */
  private lastStreamEvent?: ColimindChatStreamEvent;

  constructor() {
    // Derive the greeting name and the settings profile from the logged-in user's JWT.
    this.auth.authentication.pipe(takeUntilDestroyed()).subscribe((decoded: DecodedJwt) => {
      const sub = decoded?.sub ?? '';
      const ctx = decoded?.context ?? '';
      this.userIdentifier = sub + '_' + ctx;
      // Loads this user's stored settings; everything written afterwards is persisted for them.
      this.settings.useProfile(this.userIdentifier);
      this.jwtName.set(firstNameFromSubject(sub));
      // No language seed from the JWT here: LanguagesService already derives the app language
      // from the very same token, and ngOnInit picks it up below. Seeding twice only made the
      // stored value depend on which of the two subscriptions happened to emit last.
    });

    effect(() => {
      const sessionId = this.activeSessionId();
      if (!this.urlApplied()) return;
      const threadId = sessionId ? this.colimind.getCurrentSessionThreadId(sessionId) : '';
      const url = threadId ? `/colimind/chat/${encodeURIComponent(threadId)}` : '/colimind';
      if (this.router.url === url) return;
      this.ownNavigation = url;
      this.router.navigateByUrl(url);
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(e => {
        if (e.urlAfterRedirects === this.ownNavigation) {
          this.ownNavigation = undefined;
          return;
        }
        this.openChatFromUrl();
      });
  }

  /** Opens the chat named in the URL, e.g. after a reload, a bookmark or the back button. */
  private openChatFromUrl(): void {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let threadId: string | null = null;
    while (route && !threadId) {
      threadId = route.paramMap.get('threadId');
      route = route.firstChild;
    }

    if (threadId) {
      const sessionId = this.colimind.findSessionByThread(this.userIdentifier, threadId);
      if (sessionId) {
        this.onSelectSession(sessionId);
      } else {
        // Chats live in this browser only, a link to another user's chat cannot be opened
        this.onNewSession();
        this.showChatNotFound();
        this.ownNavigation = '/colimind';
        this.router.navigateByUrl('/colimind', { replaceUrl: true });
      }
    } else if (this.activeSessionId() && this.urlApplied()) {
      this.onNewSession();
    }
    this.urlApplied.set(true);
  }

  private showChatNotFound(): void {
    clearTimeout(this.chatNotFoundTimer);
    this.chatNotFound.set(true);
    this.chatNotFoundTimer = setTimeout(() => this.chatNotFound.set(false), CHAT_NOT_FOUND_MS);
  }

  ngOnInit(): void {
    this.openChatFromUrl();

    // The language profile follows the language selected in CoLiMo -- the settings panel has no
    // field of its own for it, but the story "answer in my language profile" still has to hold.
    // The short id ('de', 'cn') is what the JWT and the rest of the app use; localeCode would
    // hand the backend a second format ('de-DE', 'zh-CN') for the same setting.
    this.languages.currentLanguage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(language => {
        if (language?.id) this.settings.update({ languageProfile: language.id });
      });
  }

  ngOnDestroy(): void {
    clearTimeout(this.spotlightTimer);
    clearTimeout(this.chatNotFoundTimer);
    clearTimeout(this.focusTimer);
    // Leaving the page mid-request is an abort like any other -- keep and persist what arrived.
    this.streamSub?.unsubscribe();
    this.streamSub = undefined;
    this.abortPendingRequest();
  }

  /**
   * Onboarding covers the page until it has been completed once. Waiting for profileLoaded
   * keeps it from flashing up before the stored flag has been read.
   */
  showOnboarding = computed(() =>
    this.settings.profileLoaded() && (!this.settings.settings().onboardingCompleted || !this.settings.termsAccepted()));

  /** The chat exists only after the onboarding, not just hidden behind it. */
  chatReady = computed(() => this.settings.profileLoaded() && !this.showOnboarding());

  /**
   * Brief cue right after the onboarding: the input takes focus and pulses twice. Deliberately
   * no dimming overlay -- that is the pattern for multi step tours, and a full screen layer
   * right after closing a modal is a second modality for a single hint. It also proved brittle:
   * the sidenav closing animation moved the target after it had been measured.
   */
  spotlight = signal(false);
  private spotlightTimer?: ReturnType<typeof setTimeout>;
  private focusTimer?: ReturnType<typeof setTimeout>;

  /** Exactly one of the two is rendered, depending on whether the chat already has messages. */
  @ViewChild(DashboardComponent) private dashboard?: DashboardComponent;
  @ViewChild(ChatWindowComponent) private chatWindow?: ChatWindowComponent;

  onTermsAccepted(): void {
    // The onboarding is only done once the user starts chatting, also if it was finished before
    this.settings.update({ onboardingCompleted: false });
    this.settings.acceptTerms();
  }

  onOnboardingCompleted(): void {
    this.settings.completeOnboarding();
    // Replaying starts from the settings panel; close it so the chat is what the user sees.
    this.panelOpen.set(false);
    this.spotlight.set(true);

    // After the panel has finished closing, so focus does not fight the animation.
    this.focusTimer = setTimeout(() => {
      (this.dashboard ?? this.chatWindow)?.focusInput();
    }, 450);

    this.spotlightTimer = setTimeout(() => this.dismissSpotlight(), 3000);
  }

  dismissSpotlight(): void {
    if (!this.spotlight()) return;
    clearTimeout(this.spotlightTimer);
    this.spotlightTimer = undefined;
    this.spotlight.set(false);
  }

  filteredSessions = computed((): SessionListItem[] =>
    matchSessions(this.sessions(), this.searchQuery()));

  /**
   * Stores the session and keeps the panel in sync. Without the reload the history and search
   * lists only ever refresh when the panel is opened, so a session created while the panel is
   * already open stays invisible until the user closes and reopens it.
   */
  private persistSession(event: ColimindChatStreamEvent): string | null {
    if (this.messages().length === 0) return null;
    const sessionId = this.colimind.saveSession(event, this.userIdentifier ?? null, this.messages());
    if (sessionId) this.loadSessions();
    return sessionId;
  }

  private loadSessions(): void {
    this.colimind.listSessions(this.userIdentifier).subscribe({ next: s => this.sessions.set(s), error: () => this.sessions.set([]) });
  }

  openPanel(mode: PanelMode): void {
    if (this.panelOpen() && this.panelMode() === mode) {
      this.panelOpen.set(false);
      return;
    }
    this.panelMode.set(mode);
    this.panelOpen.set(true);
    if (mode === 'history' || mode === 'search') this.loadSessions();
  }

  togglePanel(): void {
    const willOpen = !this.panelOpen();
    this.panelOpen.set(willOpen);
    if (willOpen) {
      if (this.panelMode() !== 'history') this.panelMode.set('history');
      this.loadSessions();
    }
  }

  async onSend(question: string): Promise<void> {
    if (!this.chatReady()) return;
    this.chatNotFound.set(false);

    const assistantMessage: Message = {
      role: 'assistant',
      text: '',
      timestamp: new Date().toISOString()
    };

    this.messages.update(m => [
      ...m,
      {
        role: 'user',
        text: question,
        timestamp: new Date().toISOString()
      },
      assistantMessage
    ]);

    this.loading.set(true);
    this.streamStatus.set('colimind.status.thinking');
    this.pendingMessage = assistantMessage;
    this.lastStreamEvent = undefined;

    const request: ColimindChatRequest = {
      input: question,
      metadata: this.settings.toMetadata(this.colimind.getCurrentSessionThreadId(this.activeSessionId())) // add thread id to meta data of chat request to Colimind/MAUI this will preserve the current thread/session
    };

    this.streamSub = this.colimindApi.stream(request).subscribe({
      next: (event) => {

        // Remember the thread this answer belongs to, so an abort can still persist it.
        if (event.threadId) this.lastStreamEvent = event;

        switch (event.type) {

          case 'created':
            break;

          case 'itemAdded':
          case 'response.output_item.done':
            this.trackToolProgress(event);
            break;

          case 'delta':
            this.streamStatus.set(null);
            assistantMessage.text = (assistantMessage.text ?? '') + (event.text ?? '');
            this.messages.update(m => [...m]);
            break;

          case 'completed': {
            assistantMessage.response = toChatResponse(event, assistantMessage.text, this.activeSessionId());
            this.messages.update(m => [...m]);
            const eventSessionId = this.persistSession(event);
            if (eventSessionId) this.activeSessionId.set(eventSessionId);
            this.pendingMessage = undefined;
            this.loading.set(false);
            break;
          }

          case 'error':
          case 'response.failed':
            this.failPendingRequest(assistantMessage, 'server', event.raw);
            break;

          case 'response.approval_required':
            this.showAgentQuestions(event, assistantMessage);
            break;

          default:
            break;
        }
      },

      error: (error: unknown) => this.failPendingRequest(assistantMessage, 'connection', (error as Error)?.message),
      // A finished answer always sends a completed event first.
      complete: () => this.failPendingRequest(assistantMessage, 'server', 'Stream ended without a completed event'),
    });
  }

  /** Sends the answers as the next message in the same thread. */
  onAnswerQuestions(message: Message, answers: string[]): void {
    if (!message.questions?.length || message.answers) return;
    message.answers = answers;
    this.messages.update(m => [...m]);
    const text = formatAgentAnswers(message.questions, answers);
    if (text) this.onSend(text);
  }

  /** Keeps the partial answer, marks it as failed and stores it. Does nothing if the answer is already done. */
  private failPendingRequest(message: Message, kind: 'server' | 'connection', details: unknown): void {
    if (this.pendingMessage !== message) return;
    message.error = { kind, details: typeof details === 'string' ? details : JSON.stringify(details, null, 2) };
    this.streamSub?.unsubscribe();
    this.streamSub = undefined;
    this.abortPendingRequest();
    this.loading.set(false);
  }

  /** Tool calls can take a minute without any text, so show which step is running. */
  private trackToolProgress(event: ColimindChatStreamEvent): void {
    const item = (event.raw as { item?: { type?: string; name?: string } })?.item;
    if (event.type === 'itemAdded' && item?.type === 'function_call') {
      this.streamStatus.set(toolStatusKey(item.name));
    } else if (event.type === 'response.output_item.done' && item?.type === 'function_call_output') {
      this.streamStatus.set('colimind.status.thinking');
    }
  }

  /**
   * The agent asks a question. Show the question and end the request.
   * The session is stored so that the answer continues the same thread.
   */
  private showAgentQuestions(event: ColimindChatStreamEvent, assistantMessage: Message): void {
    assistantMessage.questions = parseAgentQuestions(event.raw);
    if (!assistantMessage.questions.length) {
      assistantMessage.text = [assistantMessage.text, this.translate.instant('colimind.questions.unsupported')]
        .filter(Boolean).join('\n\n');
    }
    this.messages.update(m => [...m]);
    this.streamSub?.unsubscribe();
    this.streamSub = undefined;
    this.pendingMessage = undefined;
    this.loading.set(false);
    if (this.lastStreamEvent?.threadId) {
      const sessionId = this.persistSession(this.lastStreamEvent);
      if (sessionId) this.activeSessionId.set(sessionId);
    }
  }

  onStop(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = undefined;
    this.abortPendingRequest();
    this.loading.set(false);
  }

  /**
   * Defined behaviour when a running request is cut short -- by the stop button, a session
   * switch, a new chat or leaving the page: the partial answer is kept and marked as aborted
   * instead of passing for a complete one, and it is persisted so the chat is not lost.
   * Persisting requires a stream event that named its thread; without one there is nothing to
   * store the answer under and it stays in the current view only.
   */
  private abortPendingRequest(): void {
    const pending = this.pendingMessage;
    this.pendingMessage = undefined;
    if (!pending) return;

    pending.aborted = true;
    this.messages.update(m => [...m]);

    if (!this.lastStreamEvent?.threadId) return;
    const sessionId = this.persistSession(this.lastStreamEvent);
    if (sessionId) this.activeSessionId.set(sessionId);
  }

  onDemo(demo: { question: string; response: ChatResponse }): void {
    const timestamp = new Date().toISOString();
    this.messages.set([
      { role: 'user', text: demo.question, timestamp },
      { role: 'assistant', text: demo.response.answer_text, response: demo.response, timestamp },
    ]);

    // A demo answer never reaches the backend, so nothing would ever persist it. Storing it like
    // a real chat makes the whole session flow -- history, search, restore, delete -- usable
    // while the backend is unavailable.
    const seconds = Math.floor(Date.now() / 1000);
    this.persistSession({
      type: 'completed',
      threadId: `demo_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      metaData: { prompt: demo.question, createdAt: seconds, completedAt: seconds },
    });
    // activeSessionId stays untouched on purpose: it feeds thread_id into the next
    // real request, and a synthetic demo thread has no meaning to the backend.
  }

  onNewSession(): void {
    this.onStop();
    this.chatNotFound.set(false);
    this.activeSessionId.set(undefined);
    this.messages.set([]);
  }

  onSelectSession(sessionId: string): void {
    if (sessionId === this.activeSessionId()) return;
    this.onStop();
    this.chatNotFound.set(false);
    this.activeSessionId.set(sessionId);
    this.colimind.getSession(sessionId).subscribe({
      // Shallow-copy each message: `response` (table/chart/SQL) has to survive the restore, and
      // ChatWindowComponent detects a session switch by the identity of the first message.
      next: detail => this.messages.set(detail?.messages?.map(m => ({ ...m })) ?? []),
      error: () => this.messages.set([]),
    });
  }

  onDeleteAllSessions(): void {
    // Read from storage rather than from the signal: a stale list would leave sessions behind
    // that reappear the next time the panel is opened.
    this.colimind.listSessions(this.userIdentifier).subscribe(sessions =>
      sessions.forEach(s => this.colimind.deleteSession(s.session_id).pipe(catchError(() => EMPTY)).subscribe()),
    );
    this.activeSessionId.set(undefined);
    this.messages.set([]);
    this.sessions.set([]);
  }

  onDeleteSession(sessionId: string): void {
    this.colimind.deleteSession(sessionId).pipe(catchError(() => EMPTY)).subscribe();
    if (this.activeSessionId() === sessionId) {
      this.activeSessionId.set(undefined);
      this.messages.set([]);
    }
    this.sessions.update(s => s.filter(x => x.session_id !== sessionId));
  }

  onSaveAsSuggestion(promptText: string): void {
    if (!this.settings.canAddSuggestion()) return;
    this.settings.setDraft(promptText, promptText);
    this.panelMode.set('settings');
    this.panelOpen.set(true);
  }
}
